(() => {
  if (window.__HARDRIP_NATIVE_VIDEO_V166__) return;
  window.__HARDRIP_NATIVE_VIDEO_V166__ = true;

  const VIDEO_SELECTOR = 'main media-gallery video.hardrip-product-video-v165';
  const GALLERY_SELECTOR = 'main media-gallery';
  const boundGalleries = new WeakSet();
  const observedVideos = new WeakSet();
  const internalAudioChanges = new WeakSet();
  const activeVideoByGallery = new WeakMap();

  let viewportObserver = null;
  let scheduledFrame = 0;

  function getVideos(root = document) {
    return Array.from(root.querySelectorAll(VIDEO_SELECTOR));
  }

  function getSoundPreference() {
    try {
      return localStorage.getItem('hardrip-product-video-sound');
    } catch (_) {
      return null;
    }
  }

  function saveSoundPreference(value) {
    try {
      localStorage.setItem('hardrip-product-video-sound', value);
    } catch (_) {}
  }

  function setMuted(video, muted) {
    internalAudioChanges.add(video);
    video.muted = muted;
    video.defaultMuted = muted;
    video.volume = muted ? 0 : 1;

    if (muted) video.setAttribute('muted', '');
    else video.removeAttribute('muted');

    queueMicrotask(() => internalAudioChanges.delete(video));
  }

  function prepare(video) {
    if (!(video instanceof HTMLVideoElement)) return;

    video.autoplay = true;
    video.loop = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.controls = true;

    video.setAttribute('autoplay', '');
    video.setAttribute('loop', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('preload', 'auto');
    video.setAttribute('controls', '');

    if (video.dataset.hardripSoundListener !== 'true') {
      video.dataset.hardripSoundListener = 'true';
      video.addEventListener('volumechange', () => {
        if (internalAudioChanges.has(video)) return;
        saveSoundPreference(video.muted || video.volume === 0 ? 'off' : 'on');
      });
    }
  }

  function visibleRatio(video, rootElement = null) {
    const rect = video.getBoundingClientRect();
    const rootRect = rootElement
      ? rootElement.getBoundingClientRect()
      : { left: 0, top: 0, right: window.innerWidth, bottom: window.innerHeight };

    const left = Math.max(rect.left, rootRect.left, 0);
    const right = Math.min(rect.right, rootRect.right, window.innerWidth);
    const top = Math.max(rect.top, rootRect.top, 0);
    const bottom = Math.min(rect.bottom, rootRect.bottom, window.innerHeight);

    const width = Math.max(0, right - left);
    const height = Math.max(0, bottom - top);

    return (width * height) / Math.max(1, rect.width * rect.height);
  }

  function pauseOtherVideos(keep = null) {
    getVideos().forEach((video) => {
      if (video === keep) return;
      if (!video.paused) video.pause();
    });
  }

  async function playVideo(video, requestSound = false) {
    if (!(video instanceof HTMLVideoElement)) return false;

    prepare(video);
    pauseOtherVideos(video);

    const preference = getSoundPreference();
    const mayUseSound = requestSound && preference !== 'off';

    if (mayUseSound) {
      setMuted(video, false);
      try {
        await video.play();
        saveSoundPreference('on');
        return true;
      } catch (_) {}
    }

    setMuted(video, true);

    try {
      await video.play();
      return true;
    } catch (_) {
      const retry = () => {
        if (visibleRatio(video) < 0.08) return;
        prepare(video);
        setMuted(video, true);
        video.play().catch(() => {});
      };

      video.addEventListener('loadedmetadata', retry, { once: true });
      video.addEventListener('canplay', retry, { once: true });
      window.setTimeout(retry, 80);
      window.setTimeout(retry, 240);
      window.setTimeout(retry, 600);
      return false;
    }
  }

  function bestVisibleVideo(gallery) {
    const slider = gallery.querySelector('[id^="Slider-Gallery"]');
    const mobile = window.matchMedia('(max-width: 749px)').matches;
    let best = null;
    let bestRatio = mobile ? 0.55 : 0.12;

    getVideos(gallery).forEach((video) => {
      const ratio = visibleRatio(video, slider || null);
      if (ratio > bestRatio) {
        best = video;
        bestRatio = ratio;
      }
    });

    return best;
  }

  function resetVideo(video) {
    if (!(video instanceof HTMLVideoElement)) return;

    if (!video.paused) video.pause();

    try {
      video.currentTime = 0;
    } catch (_) {}
  }

  function evaluateGallery(gallery, requestSound = false) {
    const video = bestVisibleVideo(gallery);
    const previousVideo = activeVideoByGallery.get(gallery) || null;

    if (!video) {
      if (previousVideo) resetVideo(previousVideo);

      getVideos(gallery).forEach((item) => {
        if (item !== previousVideo) resetVideo(item);
      });

      activeVideoByGallery.delete(gallery);
      return;
    }

    if (previousVideo && previousVideo !== video) {
      resetVideo(previousVideo);
    }

    if (previousVideo !== video) {
      try {
        video.currentTime = 0;
      } catch (_) {}

      activeVideoByGallery.set(gallery, video);
    }

    void playVideo(video, requestSound);
  }

  function scheduleEvaluation() {
    if (scheduledFrame) cancelAnimationFrame(scheduledFrame);

    scheduledFrame = requestAnimationFrame(() => {
      scheduledFrame = 0;
      document.querySelectorAll(GALLERY_SELECTOR).forEach((gallery) => {
        evaluateGallery(gallery, false);
      });
    });
  }

  function bindGallery(gallery) {
    if (!(gallery instanceof HTMLElement) || boundGalleries.has(gallery)) return;
    boundGalleries.add(gallery);

    const slider = gallery.querySelector('[id^="Slider-Gallery"]');
    const sliderComponent = gallery.querySelector('slider-component[id^="GalleryViewer"]');

    slider?.addEventListener('scroll', scheduleEvaluation, { passive: true });

    slider?.addEventListener('touchend', () => {
      evaluateGallery(gallery, true);
      window.setTimeout(() => evaluateGallery(gallery, false), 100);
      window.setTimeout(() => evaluateGallery(gallery, false), 280);
    }, { passive: true, capture: true });

    slider?.addEventListener('pointerup', () => {
      evaluateGallery(gallery, true);
      window.setTimeout(() => evaluateGallery(gallery, false), 100);
    }, { passive: true, capture: true });

    slider?.addEventListener('scrollend', () => evaluateGallery(gallery, false), { passive: true });

    sliderComponent?.addEventListener('slideChanged', () => {
      evaluateGallery(gallery, false);
      window.setTimeout(() => evaluateGallery(gallery, false), 100);
    });
  }

  function observeVideos() {
    if (!('IntersectionObserver' in window)) return;

    if (!viewportObserver) {
      viewportObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;

            const gallery = video.closest('media-gallery');
            if (!gallery) return;

            if (entry.isIntersecting) {
              evaluateGallery(gallery, false);
            } else {
              resetVideo(video);

              if (activeVideoByGallery.get(gallery) === video) {
                activeVideoByGallery.delete(gallery);
              }
            }
          });
        },
        { threshold: [0, 0.01, 0.05, 0.12, 0.25, 0.5, 0.75, 1], rootMargin: '0px' }
      );
    }

    getVideos().forEach((video) => {
      prepare(video);
      if (observedVideos.has(video)) return;
      observedVideos.add(video);
      viewportObserver.observe(video);
    });
  }

  function refresh() {
    getVideos().forEach((video) => {
      prepare(video);
      setMuted(video, true);
      video.play().catch(() => {
        video.pause();
        try { video.currentTime = 0; } catch (_) {}
      });
    });

    document.querySelectorAll(GALLERY_SELECTOR).forEach(bindGallery);
    observeVideos();
    scheduleEvaluation();
  }

  function init() {
    refresh();

    window.addEventListener('scroll', scheduleEvaluation, { passive: true });
    window.addEventListener('resize', scheduleEvaluation, { passive: true });
    window.addEventListener('orientationchange', () => window.setTimeout(refresh, 140));
    window.addEventListener('pageshow', () => window.setTimeout(refresh, 80));

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) getVideos().forEach(resetVideo);
      else window.setTimeout(refresh, 100);
    });

    document.addEventListener('shopify:section:load', refresh);
    document.addEventListener('product-info:loaded', () => window.setTimeout(refresh, 120));

    window.setTimeout(refresh, 180);
    window.setTimeout(scheduleEvaluation, 650);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
